import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase/config';

// Fetch all published blogs (newest first)
export const getPublishedBlogs = async () => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(blogsRef, where('published', '==', true), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching published blogs:', error);
    return [];
  }
};

// Fetch all blogs (for admin dashboard)
export const getAllBlogs = async () => {
  try {
    const blogsRef = collection(db, 'blogs');
    let snapshot;
    try {
      const q = query(blogsRef, orderBy('createdAt', 'desc'));
      snapshot = await getDocs(q);
    } catch (orderError) {
      // If ordering fails, get without ordering
      snapshot = await getDocs(blogsRef);
    }
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching all blogs:', error);
    // If collection doesn't exist yet or permission error, return empty array
    if (error.code === 'permission-denied' || 
        error.message?.includes('collection') ||
        error.message?.includes('Missing or insufficient permissions')) {
      return [];
    }
    return []; // Return empty array for other errors too
  }
};

// Fetch single blog by slug
export const getBlogBySlug = async (slug) => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(blogsRef, where('slug', '==', slug), limit(1));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error fetching blog by slug:', error);
    throw error;
  }
};

// Fetch single blog by ID
export const getBlogById = async (id) => {
  try {
    const blogRef = doc(db, 'blogs', id);
    const snapshot = await getDoc(blogRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    return {
      id: snapshot.id,
      ...snapshot.data()
    };
  } catch (error) {
    console.error('Error fetching blog by ID:', error);
    throw error;
  }
};

// Generate URL-friendly slug from title
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim('-'); // Remove leading/trailing hyphens
};

// Generate excerpt from body content
export const generateExcerpt = (body, maxLength = 150) => {
  // Remove basic HTML/markdown formatting for excerpt
  const cleanText = body
    .replace(/[*#]/g, '') // Remove markdown asterisks and hashes
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .trim();
  
  if (cleanText.length <= maxLength) {
    return cleanText;
  }
  
  return cleanText.substring(0, maxLength).trim() + '...';
};

// Create new blog post
export const createBlog = async (blogData) => {
  try {
    const slug = generateSlug(blogData.title);
    const excerpt = generateExcerpt(blogData.body);
    
    // Check if slug already exists
    const existingBlog = await getBlogBySlug(slug);
    if (existingBlog) {
      throw new Error('A blog with this title already exists. Please choose a different title.');
    }
    
    const newBlog = {
      ...blogData,
      slug,
      excerpt,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const blogsRef = collection(db, 'blogs');
    const docRef = await addDoc(blogsRef, newBlog);
    
    return {
      id: docRef.id,
      ...newBlog
    };
  } catch (error) {
    console.error('Error creating blog:', error);
    throw error;
  }
};

// Update existing blog
export const updateBlog = async (id, blogData) => {
  try {
    const blogRef = doc(db, 'blogs', id);
    
    // If title changed, regenerate slug
    let updateData = { ...blogData };
    if (blogData.title) {
      const newSlug = generateSlug(blogData.title);
      
      // Check if new slug conflicts with existing blog (excluding current one)
      const existingBlog = await getBlogBySlug(newSlug);
      if (existingBlog && existingBlog.id !== id) {
        throw new Error('A blog with this title already exists. Please choose a different title.');
      }
      
      updateData.slug = newSlug;
    }
    
    // Regenerate excerpt if body changed
    if (blogData.body) {
      updateData.excerpt = generateExcerpt(blogData.body);
    }
    
    updateData.updatedAt = serverTimestamp();
    
    await updateDoc(blogRef, updateData);
    
    // Return updated blog
    return await getBlogById(id);
  } catch (error) {
    console.error('Error updating blog:', error);
    throw error;
  }
};

// Delete blog
export const deleteBlog = async (id) => {
  try {
    const blogRef = doc(db, 'blogs', id);
    
    // Get blog data to check for featured image
    const blog = await getBlogById(id);
    
    // Delete featured image if it exists
    if (blog?.featuredImage) {
      try {
        await deleteBlogImage(blog.featuredImage);
      } catch (imageError) {
        console.warn('Could not delete featured image:', imageError);
      }
    }
    
    await deleteDoc(blogRef);
  } catch (error) {
    console.error('Error deleting blog:', error);
    throw error;
  }
};

// Upload blog image to Firebase Storage
export const uploadBlogImage = async (file) => {
  try {
    
    if (!file) {
      throw new Error('No file provided');
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image must be smaller than 5MB');
    }
    
    // Generate unique filename
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    
    const imageRef = ref(storage, `blog-images/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(imageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading blog image:', error);
    throw error;
  }
};

// Delete blog image from Firebase Storage
export const deleteBlogImage = async (imageUrl) => {
  try {
    if (!imageUrl) return;
    
    // Extract path from Firebase Storage URL
    const pathStart = imageUrl.indexOf('/blog-images/');
    if (pathStart === -1) return; // Not a blog image URL
    
    const pathEnd = imageUrl.indexOf('?');
    const path = imageUrl.substring(pathStart + 1, pathEnd > -1 ? pathEnd : undefined);
    
    const imageRef = ref(storage, path);
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting blog image:', error);
    throw error;
  }
};

// Fetch blogs pending AI review
export const getPendingReviewBlogs = async () => {
  try {
    const blogsRef = collection(db, 'blogs');
    const q = query(
      blogsRef,
      where('reviewStatus', '==', 'pending_review'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching pending review blogs:', error);
    return [];
  }
};

// Approve a pending review blog (publish it)
export const approveBlog = async (id) => {
  try {
    const blogRef = doc(db, 'blogs', id);
    await updateDoc(blogRef, {
      published: true,
      reviewStatus: 'approved',
      updatedAt: serverTimestamp()
    });
    return await getBlogById(id);
  } catch (error) {
    console.error('Error approving blog:', error);
    throw error;
  }
};

// Convert markdown tables to styled HTML tables
const formatMarkdownTables = (content) => {
  return content.replace(
    /((?:^\|.+\|$\n?)+)/gm,
    (tableBlock) => {
      const rows = tableBlock.trim().split('\n').filter(r => r.trim());
      if (rows.length < 2) return tableBlock;

      // Check if second row is a separator (|---|---|)
      const isSeparator = (row) => /^\|[\s-:|]+\|$/.test(row);
      const hasSeparator = isSeparator(rows[1]);

      const parseRow = (row) =>
        row.split('|').slice(1, -1).map(cell => cell.trim());

      let html = '<div class="overflow-x-auto my-6"><table class="w-full border-collapse text-sm">';

      if (hasSeparator) {
        // Header row
        const headers = parseRow(rows[0]);
        html += '<thead><tr>';
        headers.forEach(h => {
          html += `<th class="border border-gray-600 bg-gray-800 px-4 py-2 text-left text-yellow-400 font-semibold">${h}</th>`;
        });
        html += '</tr></thead>';

        // Data rows (skip separator)
        html += '<tbody>';
        for (let i = 2; i < rows.length; i++) {
          if (isSeparator(rows[i])) continue;
          const cells = parseRow(rows[i]);
          html += '<tr>';
          cells.forEach(c => {
            html += `<td class="border border-gray-700 px-4 py-2 text-gray-300">${c}</td>`;
          });
          html += '</tr>';
        }
        html += '</tbody>';
      } else {
        // No header — all body rows
        html += '<tbody>';
        rows.forEach(row => {
          if (isSeparator(row)) return;
          const cells = parseRow(row);
          html += '<tr>';
          cells.forEach(c => {
            html += `<td class="border border-gray-700 px-4 py-2 text-gray-300">${c}</td>`;
          });
          html += '</tr>';
        });
        html += '</tbody>';
      }

      html += '</table></div>';
      return html;
    }
  );
};

// Format blog content for display (convert markdown-style to HTML)
export const formatBlogContent = (content) => {
  if (!content) return '';

  // Process tables BEFORE line breaks get converted
  let processed = formatMarkdownTables(content);

  return processed
    // Convert [text](url) markdown links to clickable links (process first to avoid conflicts)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-yellow-400 hover:text-yellow-300 underline">$1</a>')
    // Convert *link* to clickable links (process before bold to avoid conflicts)
    .replace(/\*([^*]+)\*/g, (match, url) => {
      // Check if the content looks like a URL
      if (url.includes('.') && (url.startsWith('http') || url.startsWith('www.') || url.match(/^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/))) {
        // Add protocol if missing
        const fullUrl = url.startsWith('http') ? url : `https://${url}`;
        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-yellow-400 hover:text-yellow-300 underline">${url}</a>`;
      }
      // If it doesn't look like a URL, just return the original text with asterisks
      return match;
    })
    // Convert ***italic*** to <em>
    .replace(/\*\*\*(.*?)\*\*\*/g, '<em>$1</em>')
    // Convert **bold** to <strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert __underline__ to <u>
    .replace(/__(.*?)__/g, '<u>$1</u>')
    // Convert # Heading to <h3>
    .replace(/^# (.*$)/gm, '<h3>$1</h3>')
    // Convert ## Heading to <h4>
    .replace(/^## (.*$)/gm, '<h4>$1</h4>')
    // Convert line breaks to <br>
    .replace(/\n/g, '<br>')
    // Convert double line breaks to paragraphs
    .replace(/<br><br>/g, '</p><p>')
    // Wrap content in paragraphs
    .replace(/^(.*)$/, '<p>$1</p>')
    // Clean up empty paragraphs
    .replace(/<p><\/p>/g, '')
    // Fix heading paragraphs
    .replace(/<p>(<h[3-4]>.*?<\/h[3-4]>)<\/p>/g, '$1');
};